import React from 'react'
import { Transaction } from '../../../types/intellijetnet'
import { AircraftOverride } from '../../../types/database'
import { ExportOptions } from '../ExportOptionsModal'
import { formatPrice, formatAskingPrice, formatTakePrice, formatModifier, formatDate, formatRelativeValue as formatRelativeValueUtil } from '../../../utils/formatters'
import { OptionLeadersList } from '../../aircraft/OptionLeadersList'
import { RelativeValueBreakdown } from '../../../hooks/useRelativeValue'

/**
 * Get the short model name with plural "s" suffix.
 * E.g., "Gulfstream G550" => "G550s", "G550" => "G550s"
 */
export const getModelShortPlural = (modelName: string): string => {
  const trimmed = modelName.trim()
  const tokens = trimmed.split(/\s+/)
  const shortModel = tokens[tokens.length - 1] || trimmed
  if (shortModel.toLowerCase().endsWith('s')) {
    return shortModel
  }
  return `${shortModel}s`
}

/**
 * Format date for cover page header (e.g., "Feb 5th, 2026")
 */
export const formatCoverDate = (): string => {
  const now = new Date()
  const month = now.toLocaleDateString('en-US', { month: 'short' })
  const day = now.getDate()
  const year = now.getFullYear()
  const ordinal = (d: number) => {
    if (d > 3 && d < 21) return 'th'
    switch (d % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }
  return `${month} ${day}${ordinal(day)}, ${year}`
}

export const getOwnerValue = (
  aircraftId: number,
  overrides: Map<number, AircraftOverride>,
  companies?: Map<number, { company_name: string }>
): string => {
  const override = overrides.get(aircraftId)
  if (override?.owner_custom) return override.owner_custom
  if (override?.owner && companies) {
    const company = companies.get(override.owner)
    return company?.company_name || '-'
  }
  return '-'
}

export const getBrokerValue = (
  aircraftId: number,
  overrides: Map<number, AircraftOverride>,
  companies?: Map<number, { company_name: string }>
): string => {
  const override = overrides.get(aircraftId)
  if (override?.broker_custom) return override.broker_custom
  if (override?.broker && companies) {
    const company = companies.get(override.broker)
    return company?.company_name || '-'
  }
  return '-'
}

export const formatDamageHistory = (damageHistory: string | null): string => {
  if (damageHistory === null || damageHistory === undefined) return '-'
  const upperCase = damageHistory.toUpperCase()
  if (upperCase === 'Y' || upperCase === 'YES' || upperCase === 'TRUE' || upperCase === '1') return 'YES'
  if (upperCase === 'N' || upperCase === 'NO' || upperCase === 'FALSE' || upperCase === '0') return 'NO'
  if (upperCase === 'U' || upperCase === 'UNKNOWN') return 'UNK'
  return damageHistory.toUpperCase()
}

export const renderOptions = (aircraftOptions: Array<{ name: string; value: number }>): React.ReactNode => {
  if (!aircraftOptions.length) return '-'
  return <OptionLeadersList options={aircraftOptions} />
}

export const formatDateListed = (dateListed: string | null): string => {
  if (!dateListed) return '-'
  const dateText = formatDate(dateListed)
  const listDate = new Date(dateListed)
  const today = new Date()
  const daysDiff = Math.floor((today.getTime() - listDate.getTime()) / (1000 * 60 * 60 * 24))
  return `${dateText}\n${daysDiff}`
}

export const formatSoldDates = (dateListed: string | null, dateSold: string | null, aircraftTransaction?: any | null): string => {
  const effectiveDateListed = aircraftTransaction?.date_listed || dateListed
  const effectiveDateSold = aircraftTransaction?.transaction_date || dateSold
  const listedText = effectiveDateListed ? formatDate(effectiveDateListed) : '-'
  const soldText = effectiveDateSold ? formatDate(effectiveDateSold) : '-'
  return `${listedText}\n${soldText}`
}


export const formatRelativeValue = (
  aircraftId: number,
  relativeValues: Map<number, number | null>,
  relativeValueBreakdowns: Map<number, RelativeValueBreakdown> | undefined,
  missingABB: Set<number>
): React.ReactNode => {
  const value = relativeValues.get(aircraftId)
  const breakdown = relativeValueBreakdowns?.get(aircraftId)

  if (missingABB.has(aircraftId)) return 'Missing ABB'
  if (value === null || value === undefined) return '-'

  const formatAdjustment = (val: number): React.ReactNode => {
    if (val === 0) return '$0'
    const absValue = Math.abs(val)
    const formatted = formatRelativeValueUtil(absValue)
    if (val < 0) {
      return <span className="text-red-600">({formatted})</span>
    }
    return formatted
  }

  if (breakdown) {
    return (
      <div className="ij-rv-breakdown leading-tight">
        <div className="ij-rv-total text-right font-bold mb-0.5">
          {formatRelativeValueUtil(value)}
        </div>
        <div className="ij-rv-sections">
          <div className="ij-rv-line">
            <span className="ij-rv-label">ABB Base Value:</span>
            <span className="ij-rv-value">{breakdown.abbValue !== null ? formatRelativeValueUtil(breakdown.abbValue) : '-'}</span>
          </div>
          <div className="ij-rv-line">
            <span className="ij-rv-label">Options:</span>
            <span className="ij-rv-value">{breakdown.optionsTotal !== 0 ? formatRelativeValueUtil(breakdown.optionsTotal) : '-'}</span>
          </div>
          <div className="ij-rv-line">
            <span className="ij-rv-label">AFTT:</span>
            <span className="ij-rv-value">{formatAdjustment(breakdown.maintenanceAdjustment)}</span>
          </div>
          <div className="ij-rv-line">
            <span className="ij-rv-label">EMP:</span>
            <span className="ij-rv-value">{formatAdjustment(-breakdown.empCostAdjustment)}</span>
          </div>
        </div>
      </div>
    )
  }

  return formatRelativeValueUtil(value)
}

export const formatAskingTake = (
  aircraftId: number,
  effectiveAircraft: any,
  overrides: Map<number, AircraftOverride>,
  exportOptions: ExportOptions
): React.ReactNode => {
  const override = overrides.get(aircraftId)
  const showAskingPrice = exportOptions.columns.askingPrice
  const showTakePrice = exportOptions.columns.takePrice

  if (exportOptions.pricingMode === 'external') {
    const adjAskMakeOffer = override?.adj_ask_make_offer
    const externalAskingPrice = override?.external_asking_price
    const externalTakePrice = override?.external_take_price
    const externalTakePriceModifier = override?.external_take_price_modifier

    const askingPriceValue = externalAskingPrice ?? effectiveAircraft.asking
    const takePriceValue = externalTakePrice ?? effectiveAircraft.take_price
    const takePriceModifier = externalTakePrice != null ? externalTakePriceModifier : override?.take_price_modifier

    const askingPriceText = adjAskMakeOffer ? 'M.O.' : formatAskingPrice(askingPriceValue)
    const takePriceText = takePriceValue != null ? formatTakePrice(takePriceValue) : null
    const displayModifier = formatModifier(takePriceModifier)

    const takePriceDisplay = takePriceText
      ? <>{takePriceText}{displayModifier && <span className="font-bold italic"> {displayModifier}</span>}</>
      : displayModifier
        ? <span className="font-bold italic">{displayModifier}</span>
        : '-'

    if (showAskingPrice && !showTakePrice) return <div className="text-center">{askingPriceText}</div>
    if (!showAskingPrice && showTakePrice) return <div className="text-center">{takePriceDisplay}</div>
    return <><div>{askingPriceText}</div><div>{takePriceDisplay}</div></>
  } else {
    const actualAskMakeOffer = override?.actual_ask_make_offer
    const askingPrice = actualAskMakeOffer ? 'M.O.' : formatAskingPrice(effectiveAircraft.asking)
    const takePriceModifier = override?.take_price_modifier
    const takePrice = effectiveAircraft.take_price ? formatTakePrice(effectiveAircraft.take_price) : null
    const displayModifier = formatModifier(takePriceModifier)

    const takePriceDisplay = takePrice
      ? <>{takePrice}{displayModifier && <span className="font-bold italic"> {displayModifier}</span>}</>
      : displayModifier
        ? <span className="font-bold italic">{displayModifier}</span>
        : '-'

    if (showAskingPrice && !showTakePrice) return <div className="text-center">{askingPrice}</div>
    if (!showAskingPrice && showTakePrice) return <div className="text-center">{takePriceDisplay}</div>
    return <><div>{askingPrice}</div><div>{takePriceDisplay}</div></>
  }
}

export const formatSoldPrices = (
  aircraftId: number,
  effectiveAircraft: any,
  transaction: Transaction | null,
  overrides: Map<number, AircraftOverride>,
  exportOptions: ExportOptions,
  aircraftTransaction?: any | null
): React.ReactNode => {
  const showAskingPrice = exportOptions.columns.askingPrice
  const override = overrides.get(aircraftId)

  if (exportOptions.pricingMode === 'external') {
    const adjAskMakeOffer = aircraftTransaction?.adj_ask_make_offer ?? override?.adj_ask_make_offer
    const externalAskingPrice = aircraftTransaction?.external_asking_price ?? override?.external_asking_price
    const actualAskingPrice = aircraftTransaction?.asking_price ?? effectiveAircraft.asking
    const askingPriceValue = externalAskingPrice ?? actualAskingPrice
    const askingPriceText = adjAskMakeOffer ? 'M.O.' : formatAskingPrice(askingPriceValue)
    const soldPrice = aircraftTransaction?.sale_price ?? transaction?.sold_price
    const soldPriceText = soldPrice ? formatPrice(soldPrice.toString()) : '-'

    if (!showAskingPrice) return <div className="text-center">{soldPriceText}</div>
    return <><div>{askingPriceText}</div><div>{soldPriceText}</div></>
  } else {
    const actualAskMakeOffer = aircraftTransaction?.actual_ask_make_offer ?? override?.actual_ask_make_offer
    const askingPrice = aircraftTransaction?.asking_price ?? effectiveAircraft.asking
    const askingPriceText = actualAskMakeOffer ? 'M.O.' : formatAskingPrice(askingPrice)
    const soldPrice = aircraftTransaction?.sale_price ?? transaction?.sold_price
    const soldPriceText = soldPrice ? formatPrice(soldPrice.toString()) : '-'

    if (!showAskingPrice) return <div className="text-center">{soldPriceText}</div>
    return <><div>{askingPriceText}</div><div>{soldPriceText}</div></>
  }
}
